/**
 * Safari only gained `MediaQueryList.addEventListener` in version 14 (iOS 14).
 * Older WebViews — still inside the `not safari < 10` browserslist floor this
 * app builds for — expose just the deprecated `addListener`/`removeListener`
 * pair. MUI's `useMediaQuery` calls `addEventListener('change', …)`
 * unconditionally, so on iOS 12 it threw "r.addEventListener is not a function"
 * out of the effect and the error boundary blanked the whole tree that used it
 * — TopbarDesktop, so effectively every page (VANSITE-FRONTEND-4PM).
 *
 * Bridges the modern API onto the legacy one, and only where it is missing:
 * engines that already implement `addEventListener` are left untouched.
 *
 * Patching `MediaQueryList.prototype` alone is not enough. WebKit started
 * exposing the `MediaQueryList` constructor on `window` only in the same
 * release that made it an EventTarget (Safari 14), so on exactly the engines
 * that need the bridge there is no prototype to reach — which is why 4PM kept
 * reporting from iOS 12.5 on releases that already carry the prototype patch.
 * Where the constructor is missing, the instances handed out by `matchMedia`
 * are patched instead — and only there: replacing `matchMedia` is the wider,
 * more surprising change (every caller gets a foreign function identity), so it
 * happens only when the prototype route cannot work.
 *
 * Written without optional chaining on purpose — it runs before/independently of
 * the transpiled app bundle on exactly the engines that lack modern syntax.
 */
const CHANGE = 'change';

function addEventListenerShim(type, listener) {
  if (type === CHANGE && typeof listener === 'function') {
    this.addListener(listener);
  }
}

function removeEventListenerShim(type, listener) {
  if (type === CHANGE && typeof listener === 'function') {
    this.removeListener(listener);
  }
}

/**
 * Bridges the modern listener API onto the legacy one for a single
 * MediaQueryList — or for `MediaQueryList.prototype`, which covers every
 * instance at once. No-op when the target is already modern, or has no legacy
 * API to delegate to.
 *
 * @param {Object} target
 * @returns {Object} the same target, so call sites can pass it straight on
 */
const bridgeMediaQueryList = (target) => {
  if (!target || typeof target.addListener !== 'function') {
    return target;
  }
  if (typeof target.addEventListener === 'function') {
    return target;
  }

  target.addEventListener = addEventListenerShim;
  target.removeEventListener = removeEventListenerShim;

  return target;
};

/**
 * Whether every MediaQueryList this engine hands out already answers to
 * `addEventListener` — natively, or because the prototype was just bridged.
 *
 * @param {Function} [MediaQueryListCtor] `window.MediaQueryList`, if exposed
 * @returns {boolean}
 */
const prototypeCoversEveryInstance = (MediaQueryListCtor) =>
  !!MediaQueryListCtor &&
  !!MediaQueryListCtor.prototype &&
  typeof MediaQueryListCtor.prototype.addEventListener === 'function';

const installMediaQueryListPolyfill = () => {
  if (typeof window === 'undefined') {
    return;
  }

  // Fast path for engines that do expose the constructor: one patch, every
  // instance covered.
  if (window.MediaQueryList) {
    bridgeMediaQueryList(window.MediaQueryList.prototype);
  }

  // Covered either way — natively modern, or bridged one line above. Leave
  // `matchMedia` exactly as the engine defined it.
  if (prototypeCoversEveryInstance(window.MediaQueryList)) {
    return;
  }

  // Fallback for engines without the constructor (iOS 12): patch each
  // MediaQueryList as `matchMedia` hands it out. `bridgeMediaQueryList` still
  // bails on anything modern, so a modern instance from such an engine keeps
  // its native listeners.
  if (typeof window.matchMedia !== 'function' || window.matchMedia.isVanSitePolyfilled) {
    return;
  }

  const nativeMatchMedia = window.matchMedia;
  const patchedMatchMedia = function matchMedia(query) {
    return bridgeMediaQueryList(nativeMatchMedia.call(window, query));
  };
  patchedMatchMedia.isVanSitePolyfilled = true;

  window.matchMedia = patchedMatchMedia;
};

export default installMediaQueryListPolyfill;
